import navStructure from '../resources/nav-structure';
import _ from 'lodash';

angular.module('festzelt-os.modules.navigation')
    .factory('NavHelper', [
        '$injector',
        ($injector) => new NavHelper($injector)
    ]);

class NavHelper {
    constructor($injector) {
        this.injector = $injector;
        this.transitions = this.injector.get('$transitions');
        this.translate = this.injector.get('$translate');
        this.acl = this.injector.get('Acl');
        this.rootScope = this.injector.get('$rootScope');
        this.state = this.injector.get('$state');
        this.stateParams = this.injector.get('$stateParams');
        this.api = this.injector.get('$http');
        this.usersStore = this.injector.get('Users');

        this.initializing = true;
        this.navByKey = {};
        this.currentNavigationPath = [];
        this.navStructurePromises = [];
        this.defaultNavEntry = {
            route: null,
            type: 'route',
            right: null,
            key: null,
            children: [],
            showInNavigation: true,
            hideTabNav: false,
            paramName: null,
            customAccessor: null,
            showParentTabs: false
        };
        this.navigationParams = {};

        this._prepareNavByKey(navStructure);
        this.injector.get('$q')
            .all(this.navStructurePromises)
            .then(() => {
                this.initializing = false;
                this._fireUpdatedEvent();
            });
        this._registerEventListeners();
        this._updateCurrentNavPath();
        this._updateActiveStates();
    }

    isInitializing() {
        return this.initializing;
    }

    setTranslationsParamsForEntry(entryKey, params) {
        this.navigationParams[entryKey] = params;
        const navElement = this.navByKey[entryKey];
        if (navElement) {
            this.navByKey[navElement.key] = {
                ...navElement,
                label: this.translate.instant(`navigation.structure.${navElement.key}`, params),
            }
        }
        this._fireUpdatedEvent();
    }

    triggerUpdate() {
        this._fireUpdatedEvent();
    }

    getRootNavInformation() {
        return _.filter(this.navByKey, (navElement) => (navElement.level === 1));
    }

    getCurrentNavigationElement() {
        return _.first(_.filter(this.navByKey, (navElement) => {
            return this._isNavElementActive(navElement);
        }));
    }

    getChildrenByParentKey(parentKey) {
        return _.filter(this.navByKey, (navElement) => {
            return navElement.parentKey === parentKey;
        })
    }

    getElementByKey(key) {
        return _.first(_.filter(this.navByKey, (navElement) => {
            return navElement.key === key;
        }))
    }

    getCurrentPathElements() {
        return _.cloneDeep(_.map(this.currentNavigationPath, (element) => {
            return this.navByKey[element];
        }));
    }

    updateEventInformation() {
        Promise
            .all(
                _.filter(
                    _.each(
                        this.navByKey,
                        (navElement) => {
                            if (navElement.type === 'event-selector') {
                                return this._decorateNavEntry(navElement)
                                    .then((additionalInformation) => {
                                        this.navByKey[navElement.key] = {
                                            ...this.navByKey[navElement.key],
                                            ...additionalInformation
                                        };
                                    });
                            }
                            return null;
                        }
                    )
                )
            )
            .then(() => {
                this._fireUpdatedEvent();
            });
    }

    handleElementAction(navElement, extendedParameters) {
        if (_.isUndefined(extendedParameters)) {
            extendedParameters = {};
        }

        switch (navElement.type) {
            case 'route':
                const customParams = navElement.customRouteParams ? navElement.customRouteParams : {}
                this.state.go(navElement.route, {
                    ...this.stateParams,
                    ...customParams
                }, {
                    notify: true,
                    reload: false,
                    inherit: true,
                    location: true
                });
                break;
            case 'event-selector':
                let stateToGo = this.state.current.name;
                let childStateToGo = _.get(
                    _.first(
                        _.filter(
                            this.getChildrenByParentKey(navElement.key),
                            (childElement) => {
                                return childElement.type === 'route';
                            }
                        )
                    ),
                    'route'
                );

                if (childStateToGo) {
                    stateToGo = childStateToGo;
                }

                this.state.go(stateToGo, {
                    ...this.stateParams,
                    ...extendedParameters
                }, {
                    notify: true,
                    reload: false,
                    inherit: true,
                    location: true
                });
                break;
            case 'placeholder':
                const childElements = this.getChildrenByParentKey(navElement.key);
                this.handleElementAction(_.first(childElements));
                break;
        }
    }

    _isNavElementActive(navElement) {
        switch (navElement.type) {
            case 'route':
                let active = this.state.current.name === navElement.route;
                if(navElement.customRouteParams) {
                    _.forEach(navElement.customRouteParams, (paramValue, paramKey) => {
                        if(this.state.params[paramKey] !== paramValue) {
                            active = false;
                        }
                    })
                }
                return active;
            default:
                return false;
        }
    }

    _isNavElementOnPath(navElement) {
        return this.currentNavigationPath.indexOf(navElement.key) !== -1;
    }

    _prepareNavByKey(navInformation, parent, level) {
        parent = parent || null;
        level = level || 1;

        _.each(navInformation, (navElement) => {
            if (navElement.right && !this.acl.hasRight(navElement.right)) {
                return;
            }

            const newNavEntry = {
                ...this.defaultNavEntry,
                ...navElement,
                parentKey: parent,
                label: this.translate.instant(`navigation.structure.${navElement.key}`, this.navigationParams[navElement.key] || {}),
                level
            }

            delete newNavEntry.children;

            this.navByKey[navElement.key] = newNavEntry;

            if (_.isArray(navElement.children)) {
                this._prepareNavByKey(navElement.children, navElement.key, level + 1);
            }

            this.navStructurePromises.push(this._decorateNavEntry(newNavEntry)
                .then((additionalInformation) => {
                    this.navByKey[navElement.key] = {
                        ...this.navByKey[navElement.key],
                        ...additionalInformation
                    };
                }));
        });

        this.navStructurePromises.push(
            new Promise((resolve) => {
                this.usersStore.dispatch('getLoggedIn').then((loggedIn) => {
                    if(this.usersStore.state.company.settings.core.custom_iframes.length === 0) {
                        resolve();
                        return;
                    }

                    this.navByKey['settings.custom_iframes'] = {
                        'route': null,
                        'type': 'placeholder',
                        'key': 'settings.custom_iframes',
                        'right': 'core::custom-iframes',
                        'showInNavigation': true,
                        'hideTabNav': false,
                        'paramName': null,
                        'customAccessor': null,
                        'showParentTabs': false,
                        'icon': null,
                        'tooltip': 'Settings',
                        'parentKey': 'root.settings',
                        'label': 'Custom Actions',
                        'level': 2,
                        'active': false,
                        'onPath': false
                    }

                    _.forEach(
                        this.usersStore.state.company.settings.core.custom_iframes,
                        (customIframe) => {
                            this.navByKey[`settings.custom_iframes.${customIframe.alias}`] = {
                                'route': 'app.core.custom-iframe',
                                'customRouteParams': {
                                    iframeAlias: customIframe.alias
                                },
                                'type': 'route',
                                'right': 'core::custom-iframes',
                                'key': `settings.custom_iframes.${customIframe.alias}`,
                                'showInNavigation': true,
                                'hideTabNav': false,
                                'paramName': null,
                                'customAccessor': null,
                                'showParentTabs': true,
                                'parentKey': 'settings.custom_iframes',
                                'label': customIframe.nav_label,
                                'level': 3,
                                'active': false,
                                'onPath': false
                            }
                        }
                    )
                    resolve();
                });
            })
        );
        // this.navStructurePromises.push(
        //     this._decorateNavEntry(newNavEntry)
        //     .then((additionalInformation) => {
        //         this.navByKey[navElement.key] = {
        //             ...this.navByKey[navElement.key],
        //             ...additionalInformation
        //         };
        //     })
        // );
    }

    _fireEvent(event, args) {
        let eventName = 'al-navigation-' + event;

        this.rootScope.$emit(eventName, args);
    }

    _updateActiveStates() {
        _.each(this.navByKey, (navElement, key) => {
            this.navByKey[key] = {
                ...navElement,
                active: this._isNavElementActive(navElement),
                onPath: this._isNavElementOnPath(navElement)
            }
        });
    }

    _registerEventListeners() {
        this.transitions.onSuccess(
            {},
            (trans) => {
                this._fireUpdatedEvent();

                return trans;
            }
        );
    }

    _fireUpdatedEvent() {
        this._updateCurrentNavPath();
        this._updateActiveStates();
        this._fireEvent('updated', {})
    }

    _updateCurrentNavPath() {
        const newPath = [];
        let currentState = this.getCurrentNavigationElement();

        while (currentState) {
            newPath.push(currentState.key);

            if (currentState.parentKey) {
                currentState = this.navByKey[currentState.parentKey];
            } else {
                currentState = null;
            }
        }

        this.currentNavigationPath = newPath.reverse();
    }

    _decorateNavEntry(navEntry) {
        return new Promise((resolve, reject) => {
            switch (navEntry.type) {
                case 'event-selector':
                    this.api
                        .post(
                            'fp/guestlists',
                            {
                                fields: [
                                    'id',
                                    'name',
                                    'settings_start_date',
                                    'teaser_image',
                                    'status',
                                    'deleted_at'
                                ],
                                pagination: {
                                    perPage: -1
                                },
                                sort: {
                                  settings_start_date: 'asc'
                                },
                                includeArchived: true
                            }
                        )
                        .then(({data}) => {
                            navEntry.events = _.map(data.data, (row) => {
                                return {
                                    ...row,
                                    status: _.toLower(row.status.replace(' ', '_'))
                                }
                            });
                            resolve(navEntry);
                        })
                    break;
                default:
                    resolve(navEntry);
                    break;
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/navigation/helpers/nav-helper.js